import {
  DateRange,
  DateRangePicker,
  DateRangePickerProps,
  SingleInputDateRangeField,
} from '@mui/x-date-pickers-pro';
import IncFormHelperText from 'components/forms/inc-form-helper-text/inc-form-helper-text.component';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { FieldError } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { getFormattedDate } from './date-range-base-field.utils';

export type DateRangeBaseFieldProps = {
  value: DateRange<DateTime>;
  sx?: any;
  dateRangePickerRef?: any;
  label?: string;
  helperText?: string;
  error?: FieldError;
  'data-testid'?: string;
  setOpen?: (open: boolean) => void;
} & Partial<DateRangePickerProps>;

const DateRangeBaseField = ({
  value,
  sx,
  dateRangePickerRef,
  label,
  helperText,
  error,
  'data-testid': dataTestId = 'DateFilter.DateRangePicker',
  open,
  onOpen,
  onClose,
  setOpen,
  ...rest
}: DateRangeBaseFieldProps) => {
  const { t } = useTranslation();
  const [localOpen, localSetOpen] = useState(false);
  const finalOpen = open || localOpen;
  const finalOnOpen = onOpen ? onOpen : () => localSetOpen(true);
  const finalOnClose = onClose ? onClose : () => localSetOpen(false);

  return (
    <DateRangePicker
      open={finalOpen}
      onOpen={finalOnOpen}
      onClose={finalOnClose}
      slots={{
        field: SingleInputDateRangeField,
      }}
      label={label ?? t('forms.fields.dateRange.label')}
      slotProps={{
        field: {
          readOnly: true,
          onClick: finalOnOpen,
        },
        textField: {
          inputProps: {
            value: getFormattedDate(value),
            'data-testid': `${dataTestId}.Input`,
            ref: dateRangePickerRef,
          },
          helperText: (
            <IncFormHelperText error={error} helperText={helperText} />
          ),
          sx: {
            '.MuiPickersOutlinedInput-root': {
              cursor: 'pointer',
            },
            ...sx,
          },
          error: !!error,
          size: 'small',
          margin: 'normal',
        },
        popper: {
          // @ts-ignore
          'data-testid': 'DateRangeBaseField.Dialog',
        },
        dialog: {
          // @ts-ignore
          'data-testid': 'DateRangeBaseField.Dialog',
        },
      }}
      value={value}
      disableHighlightToday
      {...rest}
    />
  );
};

export default DateRangeBaseField;
